import React, { Fragment, useCallback, useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import { handleError } from "utils";
import constants from "../../../../../constants";
import InfiniteScroll from "react-infinite-scroll-component";
import { LoaderIcon } from "react-hot-toast";
import { defaultOffice } from "Messenger/hooks/useChannelListHeaderState";
import {
  getOfficeIdsFromChannelMemberRecord,
  getDesignationIdsFromChannelMemberRecord,
  channelMatchesUrl,
} from "Messenger/hooks/channelData.helpers";
import { useGroupChannelListContext } from "@sendbird/uikit-react/GroupChannelList/context";
import {
  deletedUserDataGetForSendbird,
  shrinkUrl,
} from "Messenger/hooks/conversation/useConversation";
import NotAccessibleChatCheck from "Messenger/NotAccessibleChat";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
const CustomChannelList = ({ t, ...props }) => {
  const {
    isSdkError,
    renderChannelPreview,
    onLeaveChannel,
    renderHeader = () => {},
    filteredOffices,
    filteredRoleIds,
    staffDesignationIdsByUserId,
    staffRoleLookupBusy = false,
    currentUser,
    selectedOwner,
    externalTabActive,
    internalTabActive,
    searchText = "",
    doctorChatCheck,
    setAllChannelsOfficesForDoctor,
    toggleChat,
    isFromPatientChat = false,
    setCurrentChannel,
    isFromDashBoard = false,
    currentChannel = null,
    /** False for mounted-but-hidden tabs so sync logic does not clear another tab’s selection. */
    isTabActive = true,
    /** Channel opened from a notification; keep it selected even if filters hide it. */
    pinnedChannelUrl = "",
  } = props;

  const {
    groupChannels: allChannels,
    loadMore,
    initialized,
  } = useGroupChannelListContext();

  const loading = useMemo(() => !initialized, [initialized]);

  const loadMoreChannels = async () => {
    try {
      await loadMore();
    } catch (err) {
      handleError(err);
    }
  };

  const filterChannelsForOffices = (eachChannel) => {
    if (externalTabActive) return true;
    if (!eachChannel) return false;
    const { data, members } = eachChannel;
    const memberUserIds = members?.map((m) => m.userId);
    if (
      !filteredOffices?.length ||
      filteredOffices?.includes(constants.chat.ALL_OFFICES)
    ) {
      return true;
    } else {
      if (!data) return true;
      let parsedData = null;
      let currentChannelOffices = [];
      try {
        parsedData = JSON.parse(data);
      } catch (err) {
        handleError(err);
        return true;
      }

      if (parsedData?.officeIds) {
        return parsedData?.officeIds?.some((ofId) =>
          filteredOffices.includes(ofId),
        );
      }

      if (parsedData?.members) {
        currentChannelOffices = parsedData.members
          .filter(({ id }) => memberUserIds.includes(`${id}`))
          .flatMap((m) => getOfficeIdsFromChannelMemberRecord(m))
          .filter((val) => !!val);
        const idx = currentChannelOffices?.findIndex((ofId) =>
          filteredOffices.includes(ofId),
        );
        return idx > -1 ? true : false;
      }
    }
  };
  const filterChannelsForRoles = useCallback(
    (eachChannel) => {
      if (!internalTabActive || externalTabActive) return true;
      if (!eachChannel) return false;
      if (
        !filteredRoleIds?.length ||
        filteredRoleIds.includes(constants.chat.ALL_ROLES)
      ) {
        return true;
      }
      const roleSet = new Set(
        filteredRoleIds.map((x) => Number(x)).filter((n) => !Number.isNaN(n)),
      );
      if (!roleSet.size) return true;

      if (staffRoleLookupBusy) {
        return true;
      }

      const { data, members } = eachChannel;
      const sendbirdMembers = members ?? [];
      if (!sendbirdMembers.length) return false;

      let parsedMembersList = [];
      let parsedData = {};
      if (data) {
        try {
          parsedData = JSON.parse(data);
          parsedMembersList = Array.isArray(parsedData?.members)
            ? parsedData.members
            : [];
        } catch (err) {
          handleError(err);
        }
      }
      if (parsedData?.designationIds) {
        return parsedData?.designationIds?.some((designationId) =>
          filteredRoleIds.includes(designationId),
        );
      }
      const metaByUserId = new Map();
      for (const rec of parsedMembersList) {
        if (rec?.id == null) continue;
        metaByUserId.set(String(rec.id), rec);
      }

      const memberMatchesRole = (sbMember) => {
        const uid = sbMember?.userId != null ? String(sbMember.userId) : null;
        if (!uid) return false;
        const meta = metaByUserId.get(uid);
        const fromJson = meta
          ? getDesignationIdsFromChannelMemberRecord(meta)
          : [];
        const fromStaff =
          staffDesignationIdsByUserId instanceof Map
            ? (staffDesignationIdsByUserId.get(uid) ?? [])
            : [];
        const combined = [...new Set([...fromJson, ...fromStaff])];
        return combined.some((d) => roleSet.has(Number(d)));
      };

      return sendbirdMembers.some(memberMatchesRole);
    },
    [
      internalTabActive,
      externalTabActive,
      filteredRoleIds,
      staffDesignationIdsByUserId,
      staffRoleLookupBusy,
    ],
  );
  const filterChannelsForAccountOwners = (eachChannel) => {
    const { data } = eachChannel;
    if (!currentUser.isAccountOwner && currentUser.isStaff && data) {
      try {
        const channelOwner = JSON.parse(data).accountOwner;
        return channelOwner?.id?.toString() === selectedOwner?.id?.toString();
      } catch {
        return true;
      }
    }
    return true;
  };
  const currentUserId = useMemo(
    () => currentUser?.id?.toString(),
    [currentUser.id],
  );
  const filterByName = useCallback(
    (item) => {
      if (!searchText.trim()) return true;
      const [user] = item?.members?.filter(
        (res) => currentUserId !== res.userId,
      );
      const trimString = (val) => val.toLowerCase().trim();
      return trimString(user.nickname).includes(trimString(searchText));
    },
    [currentUserId, searchText],
  );
  const deletedLatestData = useMemo(
    () => deletedUserDataGetForSendbird(),
    [allChannels],
  );
  const deletedUserListFilter = useCallback(
    (channel) => {
      if (
        deletedLatestData?.length &&
        channel.url &&
        deletedLatestData.includes(shrinkUrl(channel.url))
      ) {
        return false;
      }
      return true;
    },
    [deletedLatestData],
  );

  const filteredChannels = useMemo(() => {
    if (allChannels === undefined) return [];

    return allChannels
      .filter(filterByName)
      .filter(filterChannelsForOffices)
      .filter(filterChannelsForRoles)
      .filter(filterChannelsForAccountOwners)
      .filter(deletedUserListFilter);
  }, [
    allChannels,
    filteredOffices,
    filteredRoleIds,
    staffDesignationIdsByUserId,
    staffRoleLookupBusy,
    selectedOwner,
    externalTabActive,
    isFromPatientChat,
    internalTabActive,
    searchText,
  ]);

  /**
   * Keep the open conversation in sync with office/search filters. When the
   * filtered list is empty or no longer contains the active channel, clear
   * selection so the right pane shows the empty state instead of a stale chat.
   * A notification deep link is pinned so filters/pagination cannot close it.
   */
  useEffect(() => {
    if (!isTabActive) return;
    if (!setCurrentChannel || isSdkError || loading) return;
    if (pinnedChannelUrl && channelMatchesUrl(currentChannel, pinnedChannelUrl)) {
      return;
    }
    if (!filteredChannels?.length) {
      // Keep the open channel when it was just created and is not in the list yet.
      if (
        currentChannel &&
        !allChannels?.some((channel) => channel.url === currentChannel.url)
      ) {
        return;
      }
      setCurrentChannel(null);
      return;
    }
    const isCurrentChannelLoadedInList =
      !!currentChannel &&
      allChannels?.some((channel) => channel.url === currentChannel.url);
    if (
      currentChannel &&
      isCurrentChannelLoadedInList &&
      !filteredChannels.some((ch) => ch.url === currentChannel.url)
    ) {
      setCurrentChannel(null);
    }
  }, [
    allChannels,
    isTabActive,
    currentChannel,
    filteredChannels,
    isSdkError,
    loading,
    pinnedChannelUrl,
    setCurrentChannel,
  ]);

  useEffect(() => {
    if (doctorChatCheck) {
      const addedOffices = new Map();
      const offices = [];
      for (const curr of allChannels || []) {
        let parsed = {};
        try {
          parsed = JSON.parse(curr?.data || "{}");
        } catch {
          continue;
        }
        const members = parsed?.members ?? [];
        for (const m of members) {
          for (const oid of getOfficeIdsFromChannelMemberRecord(m)) {
            if (oid && !addedOffices.has(oid)) {
              addedOffices.set(oid, true);
              offices.push({
                value: oid,
                label: m.officeName || String(oid),
              });
            }
          }
        }
      }
      setAllChannelsOfficesForDoctor([defaultOffice].concat(offices));
    }
  }, [allChannels]);

  useEffect(() => {
    const channelList = document.querySelector(".sendbird-channel-list");
    if (isFromDashBoard && channelList) {
      if (isSdkError || (!loading && filteredChannels?.length === 0)) {
        channelList.style.paddingRight = "0px";
        if (isFromPatientChat) {
          channelList.style.height = "415px";
        }
      } else {
        channelList.style.paddingRight = "";
      }
    }
  }, [isSdkError, loading, filteredChannels]);

  const noChatHeadingSubheading = useMemo(() => {
    if (internalTabActive) {
      return {
        heading: t("messenger.noInternalChat"),
        subheading: t("messenger.noInternalConversationDesc"),
      };
    }
    if (externalTabActive) {
      return {
        heading: t("messenger.noExternalChat"),
        subheading: t("messenger.noExternalConversationDesc"),
      };
    }
    if (doctorChatCheck) {
      return {
        heading: t("messenger.noDoctorChat"),
        subheading: t("messenger.noDoctorConversationDesc"),
      };
    }
    return {
      heading: t("messenger.noPatientChat"),
      subheading: t("messenger.noPatientConversationDesc"),
    };
  }, [doctorChatCheck, internalTabActive, externalTabActive]);

  const { isStaff } = useAccountOwnerCommon();

  return (
    <Fragment>
      <div className="channel-list-header">{renderHeader()}</div>
      {!isSdkError && loading && (
        <div
          className={
            isFromDashBoard ? "empty-channel-list h-100" : "empty-channel-list"
          }
        >
          <LoaderIcon style={{ margin: "0px auto", padding: "10px" }} />
        </div>
      )}
      {isSdkError || (!loading && filteredChannels?.length === 0) ? (
        <NotAccessibleChatCheck
          patientCheck={isFromPatientChat}
          doctorChatCheck={doctorChatCheck || isFromDashBoard}
          message={t(
            `dashBoard.${
              isStaff ? "notAccessibleModulePleaseSwitchOwner" : "notAccessible"
            }`,
          )}
        >
          <div className="h-100 d-flex align-items-center">
            <div
              className="empty-channel-list"
              style={{
                height: isFromDashBoard ? "auto" : "",
              }}
            >
              <img
                src={require("assets/images/empty-chat-img.svg").default}
                alt="no-chat"
              />
              <h3> {noChatHeadingSubheading.heading}</h3>
              <p> {noChatHeadingSubheading.subheading}</p>
            </div>
          </div>
        </NotAccessibleChatCheck>
      ) : (
        !isSdkError &&
        !loading &&
        filteredChannels?.length > 0 && (
          <div className="channel-chat-list d-" id="channel-list">
            <InfiniteScroll
              dataLength={allChannels?.length}
              hasMore={true}
              next={loadMoreChannels}
              scrollableTarget="channel-list"
              // loader={
              //   <LoaderIcon style={{ margin: "10px auto", padding: "10px" }} />
              // }
            >
              <div
                className={`default-style ${
                  isFromDashBoard
                    ? isFromPatientChat
                      ? "dashboard-patient"
                      : "dashboard"
                    : ""
                }`}
              >
                <NotAccessibleChatCheck
                  patientCheck={isFromPatientChat}
                  message={t(
                    `dashBoard.${
                      isStaff
                        ? "notAccessibleModulePleaseSwitchOwner"
                        : "notAccessible"
                    }`,
                  )}
                  doctorChatCheck={doctorChatCheck || isFromDashBoard}
                >
                  {
                    filteredChannels
                      ?.map((channel) =>
                        renderChannelPreview({
                          channel,
                          onLeaveChannel,
                          toggleChat,
                        }),
                      )
                      .concat(
                        filteredChannels?.length < 7
                          ? [
                              <div
                                key="scroll-provider"
                                style={{
                                  height: `${
                                    (7 - filteredChannels.length) * 88
                                  }px`,
                                }}
                              />,
                            ]
                          : [],
                      )
                    // The concat only adds a div to provide a scroll if less than 7 results appear and query still has some pages left.
                  }
                </NotAccessibleChatCheck>
              </div>
            </InfiniteScroll>
          </div>
        )
      )}
    </Fragment>
  );
};

export default withTranslation()(CustomChannelList);
